import { useFormContext } from 'react-hook-form';

import { Radio } from '../../../../../../components/radio';
import { StrategyFormData } from '../../../../../../providers/backtesting/types';
import { CollapsibleSection } from '../../components/collapsible-section';
import { BacktestingCheckbox } from '../../components/backtesting-checkbox';
import { BacktestingInput } from '../../components/backtesting-input';
import { BacktestingSelect } from '../../components/backtesting-select';
import { FormRow } from '../../components/form-row';
import { InputTooltip } from '../../components/input-tooltip';
import { ToggleFieldRow } from '../../components/toggle-field-row';
import { UnitValueInput } from '../../components/unit-value-input';

import { ExecutionHourGrid } from './execution-hour-grid';
import {
  NotionalScalingOption,
  notionalFieldsFromOption,
  notionalOptionFromFields,
} from './execution-units';

import './styles.scss';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] as const;

const NOTIONAL_SCALING_OPTIONS: { name: string; value: NotionalScalingOption }[] = [
  { name: 'None', value: 'None' },
  { name: 'PV', value: 'PV' },
  { name: 'HWM', value: 'HWM' },
];

const NOTIONAL_SCALING_TOOLTIP = `If set to “PV” or “HWM”, then the notional entered for a trade will correspond to a percent of PV or high-watermark respectively. For example, if the notional is 0.5 and you choose to scale with “PV”, then the actual notional of the executed trade will be 0.5*PV/Spot. Similarly for HWM. “None” means notionals are absolute amounts.`;

const EXECUTION_TIME_TOOLTIP =
  'Select one or more hours of the day (UTC) at which the trade should be executed.';

// Exchange-strikes controls. Venue and strike projection each have a single
// documented value for now (disabled selects, easy to extend to real choices
// later); time projection is the one live choice.
const EXCHANGE_VENUE_OPTIONS = [{ name: 'DERIBIT', value: 'DERIBIT' }];

const TIME_PROJECTION_OPTIONS = [
  { name: 'Weighted time', value: 'weighted_time' },
  { name: 'Weighted variance', value: 'weighted_variance' },
];

const STRIKE_PROJECTION_OPTIONS = [{ name: 'Weighted strike', value: 'weighted_strike' }];

export const ExecutionSection = () => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<StrategyFormData>();

  const execErrors = errors.execution;
  const whenExecutionWeekdays = watch('execution.whenExecutionWeekdays');
  const whenExecutionWeekdaysDays = watch('execution.whenExecutionWeekdaysDays') ?? [];
  const whenExecutionAverageIn = watch('execution.whenExecutionAverageIn');
  const whenExecutionAverageInIncludeWeekends = watch(
    'execution.whenExecutionAverageInIncludeWeekends',
  );
  const useExchangeStrikes = watch('execution.useExchangeStrikes');
  const exchangeTimeProjection = watch('execution.exchangeTimeProjection');
  const notionalScalingOption = notionalOptionFromFields(
    watch('execution.optionsNotionalScaled'),
    watch('execution.notionalScalingType'),
  );

  const updateExecutionFields = (updates: Partial<StrategyFormData['execution']>) => {
    Object.entries(updates).forEach(([field, value]) => {
      setValue(`execution.${field}` as never, value as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };

  const setExecutionField = (field: keyof StrategyFormData['execution'], value: unknown) => {
    setValue(`execution.${field}` as never, value as never, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const toggleWeekday = (day: string) => {
    const current = whenExecutionWeekdaysDays;
    const updated = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    setValue('execution.whenExecutionWeekdaysDays', updated, { shouldDirty: true });
  };

  const whenExecutionWeekdaysTooltip = () => {
    return (
      <div>
        <p>
          This is a comma separated list with the following syntax:
          <br />
          <br />
          {'"2"'}: every second selected weekday from the backtest start;
          <br />
          <br />
          {'"1,3"'}: the 1st and 3rd selected weekday of each month;
          <br />
          <br />
          {'"-1"'}: the last selected weekday of the month.
        </p>
      </div>
    );
  };

  return (
    <CollapsibleSection title="Execution*">
      <div className="field-group">
        <span className="field-label field-label--with-tooltip">
          <span>Execution time*</span>
          <InputTooltip content={EXECUTION_TIME_TOOLTIP} />
        </span>
        <ExecutionHourGrid
          value={watch('execution.executionTime') ?? ''}
          onChange={(commaString) => setExecutionField('executionTime', commaString)}
        />
        {execErrors?.executionTime?.message && (
          <span className="field-error">{execErrors.executionTime.message}</span>
        )}
      </div>

      <div className="field-group radio-section">
        <label className="field-label">When execution should take place?</label>
        <div className="radio-options">
          <div className="radio-option-block">
            <Radio
              className="backtesting-form-radio-option"
              checked={whenExecutionWeekdays}
              onChange={() =>
                updateExecutionFields({
                  whenExecutionWeekdays: true,
                  whenExecutionAverageIn: false,
                  whenExecutionAverageInIncludeWeekends: false,
                })
              }
              label={
                <div className="backtesting-form-radio-copy">
                  <span className="backtesting-form-radio-label">Weekdays</span>
                  <span className="backtesting-form-radio-description">
                    Execution will take place on specific days of the week.
                  </span>
                </div>
              }
            />
            {whenExecutionWeekdays && (
              <div className="radio-conditional-content">
                <div className="weekdays-checkboxes">
                  {WEEKDAYS.map((day) => (
                    <BacktestingCheckbox
                      key={day}
                      label={day}
                      checked={whenExecutionWeekdaysDays.includes(day.toLowerCase())}
                      onChange={() => toggleWeekday(day.toLowerCase())}
                    />
                  ))}
                </div>
                {execErrors?.whenExecutionWeekdaysDays?.message && (
                  <span className="field-error">
                    {execErrors.whenExecutionWeekdaysDays.message}
                  </span>
                )}
                <div className="field-group">
                  <BacktestingInput
                    size="lg"
                    label="On a specific weekday of the month (optional)"
                    tooltip={whenExecutionWeekdaysTooltip()}
                    placeholder="e.g. 1,3 or -1"
                    {...register('execution.whenExecutionWeekdaysTime')}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="radio-option-block">
            <Radio
              className="backtesting-form-radio-option"
              checked={whenExecutionAverageIn}
              onChange={() =>
                updateExecutionFields({
                  whenExecutionWeekdays: false,
                  whenExecutionWeekdaysDays: [],
                  whenExecutionWeekdaysTime: '',
                  whenExecutionAverageIn: true,
                })
              }
              label={
                <div className="backtesting-form-radio-copy">
                  <span className="backtesting-form-radio-label">Average In</span>
                  <span className="backtesting-form-radio-description">
                    Execution will occur either every calendar day or every weekday.
                  </span>
                </div>
              }
            />
            {whenExecutionAverageIn && (
              <div className="radio-conditional-content">
                <BacktestingCheckbox
                  label="Include weekends"
                  checked={whenExecutionAverageInIncludeWeekends}
                  onChange={() =>
                    setValue(
                      'execution.whenExecutionAverageInIncludeWeekends',
                      !whenExecutionAverageInIncludeWeekends,
                      { shouldDirty: true },
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="field-group execution-notional-field">
        <BacktestingSelect
          size="lg"
          label="Options notional scaling"
          tooltip={NOTIONAL_SCALING_TOOLTIP}
          values={NOTIONAL_SCALING_OPTIONS}
          defaultValue={
            NOTIONAL_SCALING_OPTIONS.find((option) => option.value === notionalScalingOption) ??
            NOTIONAL_SCALING_OPTIONS[0]
          }
          onChange={(value) => updateExecutionFields(notionalFieldsFromOption(value))}
          className="execution-notional-select"
        />
      </div>

      <div className="execution-subgroup">
        <span className="execution-subgroup-title">Spreads &amp; fees</span>
        <FormRow columns={4} minColumnWidth={220}>
          <ToggleFieldRow
            label="Include Perpetual Spread"
            description="Spread to be applied to the perp mid-rate when executing perpetual."
            isOn={watch('execution.includePerpetualSpread')}
            onToggle={(val) => setValue('execution.includePerpetualSpread', val)}
          >
            <UnitValueInput
              rawValue={watch('execution.perpetualSpread') ?? ''}
              onRawChange={(raw) => setExecutionField('perpetualSpread', raw)}
              scale={1}
              suffix="USD"
              placeholder="e.g. 50"
              errors={execErrors?.perpetualSpread?.message}
            />
          </ToggleFieldRow>

          <ToggleFieldRow
            label="Include Vol Spread"
            description="Spread to be applied to the strike vol when executing an option."
            isOn={watch('execution.includeVolSpread')}
            onToggle={(val) => setValue('execution.includeVolSpread', val)}
          >
            <UnitValueInput
              rawValue={watch('execution.volSpread') ?? ''}
              onRawChange={(raw) => setExecutionField('volSpread', raw)}
              scale={100}
              suffix="%"
              placeholder="e.g. 0.5"
              errors={execErrors?.volSpread?.message}
            />
          </ToggleFieldRow>

          <ToggleFieldRow
            label="Include Perpetual Fee"
            description="Fee applied to perp notional and paid in corresponding Token/USD."
            isOn={watch('execution.includePerpetualFee')}
            onToggle={(val) => setValue('execution.includePerpetualFee', val)}
          >
            <UnitValueInput
              rawValue={watch('execution.perpetualFee') ?? ''}
              onRawChange={(raw) => setExecutionField('perpetualFee', raw)}
              scale={10000}
              suffix="bps"
              placeholder="e.g. 2.5"
              errors={execErrors?.perpetualFee?.message}
            />
          </ToggleFieldRow>

          <ToggleFieldRow
            label="Include Options Fee"
            description="Fee applied to options notional and paid in USD or Token."
            isOn={watch('execution.includeOptionsFee')}
            onToggle={(val) => setValue('execution.includeOptionsFee', val)}
          >
            <UnitValueInput
              rawValue={watch('execution.optionsFee') ?? ''}
              onRawChange={(raw) => setExecutionField('optionsFee', raw)}
              scale={10000}
              suffix="bps"
              placeholder="e.g. 2.5"
              errors={execErrors?.optionsFee?.message}
            />
          </ToggleFieldRow>
        </FormRow>
      </div>

      <div className="execution-subgroup">
        <span className="execution-subgroup-title">Exchange strikes</span>
        <ToggleFieldRow
          label="Use exchange strikes"
          description="Trade real listed Deribit instruments by projecting onto exchange strikes/expiries."
          isOn={useExchangeStrikes}
          onToggle={(val) => setValue('execution.useExchangeStrikes', val)}
        />
        {useExchangeStrikes && (
          <FormRow columns={3} minColumnWidth={220}>
            <BacktestingSelect
              size="lg"
              label="Venue"
              values={EXCHANGE_VENUE_OPTIONS}
              defaultValue={EXCHANGE_VENUE_OPTIONS[0]}
              onChange={() => undefined}
              disabled
            />
            <BacktestingSelect
              size="lg"
              label="Time projection"
              values={TIME_PROJECTION_OPTIONS}
              defaultValue={
                TIME_PROJECTION_OPTIONS.find((option) => option.value === exchangeTimeProjection) ??
                TIME_PROJECTION_OPTIONS[0]
              }
              onChange={(value) => setExecutionField('exchangeTimeProjection', value)}
            />
            <BacktestingSelect
              size="lg"
              label="Strike projection"
              values={STRIKE_PROJECTION_OPTIONS}
              defaultValue={STRIKE_PROJECTION_OPTIONS[0]}
              onChange={() => undefined}
              disabled
            />
          </FormRow>
        )}
      </div>

      <div className="execution-subgroup">
        <span className="execution-subgroup-title">Settle in USD</span>
        <FormRow columns={3} minColumnWidth={220}>
          <ToggleFieldRow
            label="Convert perpetual cashflow to USD"
            description="Cashflows from perpetual trades will be converted to USD."
            isOn={watch('execution.convertPerpCashflowToUsd')}
            onToggle={(val) => setValue('execution.convertPerpCashflowToUsd', val)}
          />
          <ToggleFieldRow
            label="Options settle in USD"
            description="If set to true, all option cashflows will be converted to USD."
            isOn={watch('execution.optionsSettleInUsd')}
            onToggle={(val) => setValue('execution.optionsSettleInUsd', val)}
          />
          <ToggleFieldRow
            label="Convert funding cashflows to USD"
            description="Funding payments will be converted to USD."
            isOn={watch('execution.convertFundingCashflowsToUsd')}
            onToggle={(val) => setValue('execution.convertFundingCashflowsToUsd', val)}
          />
        </FormRow>
      </div>

      <div className="execution-subgroup">
        <span className="execution-subgroup-title">Structure behaviour</span>
        <FormRow columns={2} minColumnWidth={220}>
          <ToggleFieldRow
            label="Close structure on first expiry"
            description="When the strategy has trades with different expiries, the lot closes when the first trade expires."
            isOn={watch('execution.closeStructureOnFirstExpiry')}
            onToggle={(val) => setValue('execution.closeStructureOnFirstExpiry', val)}
          />
          <ToggleFieldRow
            label="Execute trades only once"
            description="Regardless of the execution setup, once the first trade is executed, no additional trades are placed."
            isOn={watch('execution.executeTradesOnlyOnce')}
            onToggle={(val) => setValue('execution.executeTradesOnlyOnce', val)}
          />
        </FormRow>
      </div>
    </CollapsibleSection>
  );
};
